import { useEffect, useRef, useState } from 'react';
import { AtSign, CheckCircle2, Copy, Info, Loader2, Mail, Send, X } from 'lucide-react';

import { usePiegeFocus } from '../hooks/usePiegeFocus';
import type { Role } from '../api/auth';
import {
  chargerReferencesInvitation,
  creerInvitation,
  type ReferencesInvitation,
} from '../api/invitations';

/**
 * Durée cochée à l'ouverture : le modèle fourni sélectionne 14 jours.
 *
 * La LISTE des durées, elle, vient du serveur (`dureesJours`) : une durée
 * écrite en dur dans ce fichier finirait par être refusée sans qu'on sache
 * pourquoi.
 */
const DUREE_PROPOSEE = 14;

const CLASSE_CHAMP =
  'w-full px-3 py-2.5 bg-white border border-slate-300 rounded-lg text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all';
const CLASSE_ICONE = 'w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400';

interface Proprietes {
  readonly ouvert: boolean;
  readonly onFermer: () => void;
  /** Appelé après création : l'écran parent rafraîchit sa liste d'invitations. */
  readonly onCree?: () => void;
}

/**
 * Modale « Invitation simple » : un lien d'accès, sans fiche technicien.
 *
 * Reprise du modèle fourni, à une différence près, assumée : **aucun courriel
 * n'est envoyé** (le port 25 est filtré chez l'hébergeur). Le lien est rendu une
 * seule fois, à l'administrateur, qui le transmet lui-même — le bandeau bleu du
 * modèle le dit explicitement plutôt que de promettre un envoi qui n'existe pas.
 */
export function ModaleInvitation({ ouvert, onFermer, onCree }: Proprietes) {
  const [references, setReferences] = useState<ReferencesInvitation>();
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<Role | ''>('');
  const [jours, setJours] = useState<number>(DUREE_PROPOSEE);
  const [message, setMessage] = useState('');
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState<string>();
  const [lien, setLien] = useState<{ lien: string; expireLe: string; email: string }>();
  const premierChamp = useRef<HTMLInputElement>(null);
  // Le clavier ne sort pas de la modale, et il revient d'où il venait.
  const boite = useRef<HTMLDivElement>(null);
  usePiegeFocus(boite, ouvert);

  useEffect(() => {
    if (!ouvert) return;
    const controleur = new AbortController();
    void chargerReferencesInvitation({ signal: controleur.signal })
      .then((resultat) => {
        if (resultat.ok && resultat.donnees) {
          setReferences(resultat.donnees);
          // Durée par défaut ramenée dans la liste réelle du serveur.
          setJours((actuelle) =>
            resultat.donnees?.dureesJours.includes(actuelle)
              ? actuelle
              : (resultat.donnees?.dureesJours[0] ?? actuelle),
          );
        } else {
          setErreur(resultat.message ?? 'Listes du formulaire indisponibles.');
        }
      })
      .catch(() => {
        /* abandon : modale fermée */
      });
    return () => controleur.abort();
  }, [ouvert]);

  useEffect(() => {
    if (ouvert) premierChamp.current?.focus();
  }, [ouvert]);

  const reinitialiser = () => {
    setEmail('');
    setRole('');
    setJours(DUREE_PROPOSEE);
    setMessage('');
    setErreur(undefined);
    setLien(undefined);
  };

  const fermer = () => {
    reinitialiser();
    onFermer();
  };

  const soumettre = async (evenement: React.FormEvent) => {
    evenement.preventDefault();
    if (role === '') {
      setErreur('Choisissez le rôle attribué au compte invité.');
      return;
    }

    setEnvoi(true);
    setErreur(undefined);
    const resultat = await creerInvitation({
      email: email.trim(),
      role,
      jours,
      ...(message.trim() === '' ? {} : { message: message.trim() }),
    });
    setEnvoi(false);

    if (resultat.ok && resultat.donnees) {
      // Le lien n'apparaîtra plus : c'est le seul moment pour le copier.
      setLien({
        lien: `${globalThis.location.origin}${resultat.donnees.lien}`,
        expireLe: resultat.donnees.invitation.expireLe,
        email: resultat.donnees.invitation.email,
      });
      onCree?.();
    } else {
      setErreur(resultat.message ?? 'Invitation impossible.');
    }
  };

  if (!ouvert) return null;

  const durees = references?.dureesJours ?? [DUREE_PROPOSEE];
  const roles = references?.roles ?? [];
  const messageMax = references?.messageMax ?? 500;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4"
      ref={boite}
      role="dialog"
      aria-modal="true"
      aria-labelledby="titre-modale-invitation"
      onKeyDown={(evenement) => {
        if (evenement.key === 'Escape') fermer();
      }}
    >
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg overflow-hidden border border-slate-200">
        <div className="px-6 py-5 border-b border-slate-200 flex justify-between items-center bg-slate-50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-red-100 text-red-600 flex items-center justify-center flex-shrink-0">
              <Mail className="w-5 h-5" aria-hidden="true" />
            </div>
            <div>
              <h2 id="titre-modale-invitation" className="text-lg font-bold text-slate-800">
                Invitation simple
              </h2>
              <p className="text-xs text-slate-500">
                Créez un lien d’accès à transmettre vous-même.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={fermer}
            aria-label="Fermer"
            className="text-slate-400 hover:text-red-500 hover:bg-red-50 p-1.5 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" aria-hidden="true" />
          </button>
        </div>

        {lien ? (
          <div className="p-6 space-y-4">
            <div className="flex items-start gap-3 rounded-lg bg-green-50 border border-green-200 p-4">
              <CheckCircle2 className="w-5 h-5 text-green-600 shrink-0 mt-0.5" aria-hidden="true" />
              <div>
                <p className="text-sm font-medium text-slate-800">
                  Invitation prête pour {lien.email}.
                </p>
                <p className="text-xs text-slate-600 mt-1">
                  Valable jusqu’au {lien.expireLe.slice(0, 16)}. Aucun courriel n’est envoyé :
                  transmettez ce lien vous-même. Il ne sera plus affiché, et il ne sert qu’une fois.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <input
                readOnly
                value={lien.lien}
                aria-label="Lien d’invitation"
                onFocus={(evenement) => evenement.target.select()}
                className="flex-1 px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-xs text-slate-700 mono"
              />
              <button
                type="button"
                onClick={() => void navigator.clipboard?.writeText(lien.lien)}
                className="inline-flex items-center gap-1 px-3 py-2 border border-slate-300 text-slate-700 text-xs rounded-lg hover:bg-slate-50"
              >
                <Copy className="w-3.5 h-3.5" aria-hidden="true" />
                Copier
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={(evenement) => void soumettre(evenement)} className="p-6 space-y-5">
            <div>
              <label
                htmlFor="invite-email"
                className="block text-sm font-medium text-slate-700 mb-1"
              >
                Adresse email du destinataire <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <AtSign className={CLASSE_ICONE} aria-hidden="true" />
                <input
                  id="invite-email"
                  ref={premierChamp}
                  type="email"
                  required
                  value={email}
                  onChange={(evenement) => setEmail(evenement.target.value)}
                  placeholder="nom@entreprise.com"
                  className={`${CLASSE_CHAMP} pl-10`}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label
                  htmlFor="invite-role"
                  className="block text-sm font-medium text-slate-700 mb-1"
                >
                  Rôle attribué <span className="text-red-500">*</span>
                </label>
                {/* Pas de `required` ici : le navigateur bloquerait la soumission
                    sans rien dire. Le refus est prononcé par l'écran, en français,
                    avec le champ nommé. */}
                <select
                  id="invite-role"
                  value={role}
                  onChange={(evenement) => setRole(evenement.target.value as Role | '')}
                  className={CLASSE_CHAMP}
                >
                  <option value="">Sélectionnez...</option>
                  {roles.map((entree) => (
                    <option key={entree.code} value={entree.code}>
                      {entree.libelle}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label
                  htmlFor="invite-expiry"
                  className="block text-sm font-medium text-slate-700 mb-1"
                >
                  Validité du lien
                </label>
                <select
                  id="invite-expiry"
                  value={jours}
                  onChange={(evenement) => setJours(Number(evenement.target.value))}
                  className={CLASSE_CHAMP}
                >
                  {durees.map((duree) => (
                    <option key={duree} value={duree}>
                      {duree} jours
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label
                htmlFor="invite-message"
                className="flex justify-between text-sm font-medium text-slate-700 mb-1"
              >
                <span>Message personnalisé</span>
                <span className="text-xs text-slate-400 font-normal">Optionnel</span>
              </label>
              <textarea
                id="invite-message"
                rows={3}
                maxLength={messageMax}
                value={message}
                onChange={(evenement) => setMessage(evenement.target.value)}
                placeholder="Ajoutez un message qui sera affiché sur la page du lien…"
                className={`${CLASSE_CHAMP} resize-none`}
              />
              <p className="mt-1 text-xs text-slate-400">
                {message.length} / {messageMax} caractères
              </p>
            </div>

            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-800 flex items-start gap-2">
              <Info className="w-4 h-4 flex-shrink-0 mt-0.5" aria-hidden="true" />
              <p>
                Aucun courriel n’est envoyé : le lien sécurisé s’affiche <strong>une fois</strong>{' '}
                après création, à vous de le transmettre. L’invité y choisira son mot de passe.
              </p>
            </div>

            {erreur && (
              <p role="alert" className="text-sm text-red-700">
                {erreur}
              </p>
            )}

            <div className="pt-2 -mx-6 -mb-6 px-6 py-4 border-t border-slate-200 bg-slate-50 flex justify-end gap-3">
              <button
                type="button"
                onClick={fermer}
                className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-100 transition-colors"
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={envoi}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors shadow-sm flex items-center gap-2 disabled:opacity-75"
              >
                {envoi ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
                    Envoi en cours...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" aria-hidden="true" />
                    Créer le lien d’invitation
                  </>
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
